import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Rating,
  TextField,
  Box,
  Typography,
  CircularProgress
} from '@mui/material';
import ReviewService from '../../../services/review.service';
import { theme } from '../../../theme';

const ReviewModal = ({ open, onClose, appointment, salonId, salonName, onSuccess }) => {
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      setRating(0);
      setComment('');
      setError(null);
    }
  }, [open]);

  const handleSubmit = async () => {
    if (!rating) {
      setError('Veuillez sélectionner une note.');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const reviewData = {
        rating: rating,
        comment: comment
      };

      if (appointment) {
        // Handle case where salon is an object or just an ID
        const sId = appointment.salon?.salon_id || appointment.salon?.id || appointment.salon;
        reviewData.salon = sId;
        reviewData.appointment = appointment.appointment_id;
      } else if (salonId) {
        reviewData.salon = salonId;
      } else {
        throw new Error('Salon ID is required');
      }

      await ReviewService.createReview(reviewData);
      onSuccess();
      onClose();
    } catch (err) {
      console.error('Error submitting review:', err);
      setError('Une erreur est survenue lors de l\'envoi de votre avis. Veuillez réessayer.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  const displayName = salonName || appointment?.salon_name || appointment?.salon?.name || 'le salon';

  return (
    <Dialog
      open={open}
      onClose={submitting ? null : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: '16px' }
      }}
    >
      <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
        <Typography variant="h5" component="div" sx={{ fontWeight: 600, color: theme.colors.primary[700] }}>
          Votre avis compte !
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Comment s'est passé votre expérience chez {displayName} ?
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, py: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
            <Typography component="legend">Note globale</Typography>
            <Rating
              name="simple-controlled"
              value={rating}
              onChange={(event, newValue) => {
                setRating(newValue);
              }}
              size="large"
              sx={{
                fontSize: '3rem',
                color: theme.colors.primary[500]
              }}
            />
          </Box>

          <TextField
            fullWidth
            label="Votre commentaire (optionnel)"
            multiline
            rows={4}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Partagez votre expérience..."
            variant="outlined"
            disabled={submitting}
          />

          {error && (
            <Typography color="error" variant="body2">
              {error}
            </Typography>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, justifyContent: 'center', gap: 2 }}>
        <Button
          onClick={onClose}
          disabled={submitting}
          variant="outlined"
          sx={{ borderRadius: '25px', px: 4 }}
        >
          Plus tard
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={submitting || !rating}
          variant="contained"
          sx={{
            borderRadius: '25px',
            px: 4,
            backgroundColor: theme.colors.primary[500],
            color: 'white',
            '&:hover': { backgroundColor: theme.colors.primary[600] }
          }}
        >
          {submitting ? <CircularProgress size={24} color="inherit" /> : 'Envoyer mon avis'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReviewModal;
